import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  output,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { type OutputProperty } from '@blaze-shared/api-contracts-domain';
import { SelectComponent } from '@blaze-shared/shared-feature';
import { ButtonIconComponent } from '@blaze-shared/shared-ui';

import {
  parseOutputParameterFromFormValue,
  parseOutputParameterToFormValue,
} from '../openai-connector-functions.helpers';
import { OUTPUT_DATA_TYPE_OPTIONS } from '../openai-connector-functions.models';

interface ParameterRow {
  readonly uid: number;
  property: OutputProperty;
}

const OUTGOING_DESCRIPTIONS: Record<string, string> = {
  String: 'Explain the output text : “Ticket summary (≤100 words).”',
  Int: 'Explain the numeric output: “Order total in USD, no symbols.”',
  Long: 'Explain the numeric output: “Order total in USD, no symbols.”',
  Double: 'Explain the numeric output: “Order total in USD, no symbols.”',
  Decimal: 'Explain the numeric output: “Order total in USD, no symbols.”',
  Time: 'Explain the time: “Meeting start HH:MM.”',
  DateTime: 'Explain date & time: “Appointment start, ISO 8601.”',
  Date: 'Explain the date only: “Invoice due YYYY-MM-DD.”',
  Enum: 'Explain what the selection means: “Task priority label.”',
  Duration: 'Explain duration: “Video length in seconds.”',
  Email: 'Explain email output: “Customer contact email.”',
  Bool: 'Explain true/false flag:”Is overdue? true/false.”',
  Item: 'Explain the object: “Shipping address of the order.”',
};

const NUMERIC_DATA_TYPES: readonly string[] = ['Int', 'Long', 'Double', 'Decimal'];

let nextRowUid = 0;

@UntilDestroy()
@Component({
  selector: 'blaze-openai-connector-output-parameters',
  templateUrl: './openai-connector-output-parameters.component.html',
  styleUrls: ['./openai-connector-output-parameters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    ButtonIconComponent,
    SelectComponent,
  ],
})
export class OpenAIConnectorOutputParametersComponent implements OnInit, AfterViewInit {
  isValid = output<boolean>();
  removeParameter = output<void>();

  @Input() properties: OutputProperty[] = [];
  @Input() property?: OutputProperty;
  @Input() sectionTitle: string;
  /**
   * The view mode for the output parameters.
   * 'list' for displaying a list of output parameters,
   * list uses properties and valuesChanged
   * 'parameter' for displaying a single output parameter.
   * parameter uses property, removeParameter, and parameterValueChanged
   */
  @Input() view: 'list' | 'parameter';

  @Output() parameterValueChanged = new EventEmitter<OutputProperty>();
  @Output() valuesChanged = new EventEmitter<OutputProperty[]>();

  @ViewChild('outputParameterNameInput')
  outputParameterNameInput?: ElementRef<HTMLInputElement>;

  private readonly rowsValidity = new Map<number, boolean>();
  protected readonly outputDataTypeOptions = OUTPUT_DATA_TYPE_OPTIONS;
  protected readonly outgoingDescription = OUTGOING_DESCRIPTIONS;
  /**
   * Initialized once from the properties input, then the source of truth
   * The uid keeps @for tracking stable, so removals never shift row forms
   */
  protected readonly rows = signal<ParameterRow[]>([]);

  paramWrapped = signal(true);

  propertyForm = this.createFormGroup();

  isFormValid = signal(false);
  isChildValid = signal(true);

  constructor() {
    effect(() => {
      this.isValid.emit(this.isFormValid() && this.isChildValid());
    });
  }

  ngOnInit(): void {
    if (this.view === 'list') {
      this.rows.set(this.properties.map((property) => ({ uid: nextRowUid++, property })));
    }
  }

  ngAfterViewInit(): void {
    // Initialize the form with the function data
    const newValue = !this.property?.name;
    if (newValue) {
      // If the function is empty, initialize with default values
      this.paramWrapped.set(false);
    } else if (this.property) {
      this.propertyForm.patchValue(parseOutputParameterToFormValue(this.property));
    }

    this.isFormValid.set(this.propertyForm.valid || this.view === 'list');
    this.propertyForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const newValue = parseOutputParameterFromFormValue(value);
      this.parameterValueChanged.emit(newValue);
      this.isFormValid.set(this.propertyForm.valid);
    });

    if (this.view === 'parameter' && !this.propertyForm.get('name')?.value) {
      this.outputParameterNameInput?.nativeElement.focus();
    }
  }

  addOutputParameter(): void {
    const row: ParameterRow = { uid: nextRowUid++, property: { name: '', dataType: 'String' } };
    this.rowsValidity.set(row.uid, false);
    this.rows.update((rows) => [...rows, row]);
    this.emitRows();
    this.updateChildValidity();
  }

  removeRow(uid: number): void {
    this.rows.update((rows) => rows.filter((row) => row.uid !== uid));
    this.rowsValidity.delete(uid);
    this.emitRows();
    this.updateChildValidity();
  }

  updateRow(uid: number, property: OutputProperty): void {
    this.rows.update((rows) => rows.map((row) => (row.uid === uid ? { ...row, property } : row)));
    this.emitRows();
  }

  updateRowValidity(uid: number, isValid: boolean): void {
    this.rowsValidity.set(uid, isValid);
    this.updateChildValidity();
  }

  wrapToggle(): void {
    this.paramWrapped.set(!this.paramWrapped());
  }

  removeOutputParameter(): void {
    this.removeParameter.emit();
  }

  updateParameters(value: OutputProperty[]): void {
    this.propertyForm.controls['properties'].setValue(value);
  }

  private emitRows(): void {
    this.valuesChanged.emit(this.rows().map((row) => row.property));
  }

  private updateChildValidity(): void {
    this.isChildValid.set([...this.rowsValidity.values()].every(Boolean));
  }

  private createFormGroup(): FormGroup {
    const fg = new FormGroup(
      {
        name: new FormControl('', [Validators.required]),
        dataType: new FormControl('', [Validators.required]),
        description: new FormControl(''),
        isOptional: new FormControl(false),
        multipleValues: new FormControl(false),
        pattern: new FormControl(undefined),
        enums: new FormControl(undefined),
        max: new FormControl(undefined),
        min: new FormControl(undefined),
        maxLength: new FormControl(undefined),
        minLength: new FormControl(undefined),
        properties: new UntypedFormControl(undefined),
      },
      { validators: itemPropertiesValidator },
    );

    fg.controls['dataType']?.valueChanges?.pipe(untilDestroyed(this)).subscribe((dataType) => {
      // Handle dataType changes
      if (dataType !== 'String') {
        fg.controls['pattern'].setValue(undefined);
      }
      if (!NUMERIC_DATA_TYPES.includes(dataType ?? '')) {
        fg.controls['max'].setValue(undefined);
        fg.controls['min'].setValue(undefined);
      }
      if (dataType !== 'Enum') {
        fg.controls['enums'].setValue(undefined);
        fg.controls['enums'].clearValidators();
      } else {
        fg.controls['enums'].setValidators([Validators.required]);
      }
      fg.updateValueAndValidity();
    });
    fg.controls['multipleValues']?.valueChanges
      ?.pipe(untilDestroyed(this))
      .subscribe((isMultiple) => {
        if (!isMultiple) {
          fg.controls['maxLength'].setValue(undefined);
          fg.controls['minLength'].setValue(undefined);
        }
      });
    return fg;
  }
}

function itemPropertiesValidator(control: AbstractControl): ValidationErrors | null {
  const { dataType, properties } = control.value ?? {};
  return dataType === 'Item' && !properties?.length ? { itemProperties: true } : null;
}
